import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import styled from '@emotion/styled'

import ButtonBasic from 'components/common/ButtonBasic'
import Link from 'components/common/A'
import { InfoModal } from 'components/common/Modal'
import { EmailSVG, MapSVG } from 'components/common/SVGElements'
import VenderRelatedLinks from 'components/pages/Vender/VenderRelatedLinks'
import { VENDER_AD_WARNING_TEXT } from 'components/pages/Vender/constants'
import { GtmAgent } from 'utils/hooks/analytics'

const cleanContactText = (value) =>
  typeof value === 'string' ? value.trim() : ''

const buildContactAddress = (vender) => {
  const address = vender?.address || {}
  const county = cleanContactText(address.county)
  const district = cleanContactText(address.district)
  const details = cleanContactText(address.details)

  if (!vender?.isAddressPublic || !county || !district || !details) return null

  const text = `${county}${district}${details}`

  return {
    text,
    mapUrl:
      cleanContactText(vender?.mapLink) ||
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        text
      )}`,
  }
}

const buildContactLinks = (vender) =>
  (vender?.introductionInfo?.urls || [])
    .map((item, index) => ({
      id: item.id || item._id || `contact-link-${index}`,
      name: cleanContactText(item.name),
      url: cleanContactText(item.url),
    }))
    .filter((item) => item.name && item.url)
    .slice(0, 10)

export const normalizeVenderContactData = ({ vender = {} } = {}) => ({
  phone: cleanContactText(vender?.contactInfoSetting?.phone?.number),
  extension: cleanContactText(vender?.contactInfoSetting?.phone?.extension),
  email: cleanContactText(vender?.contactInfoSetting?.email?.address),
  address: buildContactAddress(vender),
  links: buildContactLinks(vender),
})

export const hasVenderContactContent = (data) =>
  Boolean(data?.phone || data?.email || data?.address || data?.links?.length)

const DetailButton = styled(Button)(({ theme }) => ({
  minWidth: 0,
  fontSize: theme.typography.body1.fontSize,
  fontWeight: theme.typography.fontWeightBold,
}))

const ContactRow = ({ label, children, alignItems = 'center' }) => (
  <Box display="flex" alignItems={alignItems} gap={4}>
    <Typography
      fontWeight="fontWeightBold"
      color="text.minus1"
      whiteSpace="nowrap"
      flexShrink={0}
    >
      {label}
    </Typography>
    <Box
      minWidth={0}
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      columnGap={1}
    >
      {children}
    </Box>
  </Box>
)

const VenderContact = ({
  data,
  vender,
  loggedIn,
  onPhoneDetailClick,
  onLoginRequired,
  onExternalLinkClick,
}) => {
  const [isEmailModalOpen, setEmailModalOpen] = useState(false)

  const onPhoneDetailButtonClick = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_contact',
      living_click_name: '聯絡電話-查看詳情',
      living_click_location: '廠商聯繫資訊頁',
      living_vender: vender?.name,
      living_vender_id: vender?._id,
    })
    onPhoneDetailClick?.()
  }

  const onEmailDetailClick = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_mail',
      living_click_name: '公司信箱-查看詳情',
      living_click_location: '廠商聯繫資訊頁',
      living_vender: vender?.name,
      living_vender_id: vender?._id,
    })
    if (loggedIn) {
      setEmailModalOpen(true)
    } else {
      onLoginRequired?.()
    }
  }

  const onMapClick = () => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'booking_service_map',
      living_click_name: '公司地址-查看詳情',
      living_click_location: '廠商聯繫資訊頁',
      living_vender: vender?.name,
      living_vender_id: vender?._id,
    })
  }

  return (
    <Box mb={18} display="flex" flexDirection="column" rowGap={2.5}>
      {!!data?.phone && (
        <ContactRow label="聯絡電話">
          <DetailButton
            variant="text"
            disableRipple
            onClick={onPhoneDetailButtonClick}
          >
            查看詳情
          </DetailButton>
        </ContactRow>
      )}

      {!!data?.email && (
        <ContactRow label="公司信箱">
          <DetailButton
            variant="text"
            disableRipple
            onClick={onEmailDetailClick}
          >
            查看詳情
          </DetailButton>
        </ContactRow>
      )}

      {!!data?.address && (
        <ContactRow label="公司地址" alignItems="flex-start">
          <Typography
            component="span"
            color="text.minus1"
            minWidth={0}
            sx={{
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {data.address.text.slice(0, -1)}
            <Box component="span" whiteSpace="nowrap">
              {data.address.text.slice(-1)}
              <IconButton
                component="a"
                href={data.address.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="在 Google 地圖查看"
                size="small"
                sx={{ p: 0, ml: 2, verticalAlign: 'middle' }}
                onClick={onMapClick}
              >
                <MapSVG primary width="24" height="24" />
              </IconButton>
            </Box>
          </Typography>
        </ContactRow>
      )}

      {!!data?.links?.length && (
        <ContactRow label="相關連結" alignItems="flex-start">
          <VenderRelatedLinks
            links={data.links}
            vender={vender}
            clickLocation="廠商聯繫資訊頁"
            onExternalLinkClick={onExternalLinkClick}
            gap={4}
          />
        </ContactRow>
      )}

      <InfoModal
        open={isEmailModalOpen}
        onClose={() => setEmailModalOpen(false)}
      >
        <Typography variant="h6" px={5} pt={5} fontWeight="fontWeightBold">
          廠商聯絡信箱
        </Typography>
        <Box p={5}>{VENDER_AD_WARNING_TEXT}</Box>
        <Box display="flex" alignItems="center" px={5} pb={2}>
          <EmailSVG $color="text.minus2" />
          <Typography ml={2}>
            <Link href={`mailto:${data?.email}`}>{data?.email}</Link>
          </Typography>
        </Box>
        <Box px={5}>
          <Typography variant="paraBodySmall" color="text.minus2">
            聯絡時，歡迎告知 {vender?.shortname} 您是從「信義居家」看到資訊。
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end" p={5}>
          <ButtonBasic
            variant="outlined"
            size="medium"
            color="neutral"
            onClick={() => setEmailModalOpen(false)}
          >
            關閉
          </ButtonBasic>
        </Box>
      </InfoModal>
    </Box>
  )
}

VenderContact.defaultProps = {
  data: {},
  loggedIn: false,
}

export default VenderContact
